import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Download, PictureAsPdf, Edit, OpenInNew } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../../../contexts/ToastContext';
import CrewClearanceChips from './CrewClearanceChips';
import { formatDateTime, formatDuration } from '../utils/crewDutyRosterUtils';
import {
  downloadAuthenticatedPdf,
  viewAuthenticatedPdf,
  openAuthenticatedPdfViaToken
} from '../../../../utils/authenticatedPdf';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

function periodStamp(hasCustomRange, startDate, endDate) {
  return hasCustomRange ? `${startDate}_to_${endDate}` : new Date().toISOString().slice(0, 10);
}

const REST_COLOR = {
  ON_DUTY: 'warning',
  RESTING: 'info',
  REST_COMPLETE: 'success',
  NO_PRIOR_DUTY: 'default'
};

const REST_LABEL = {
  ON_DUTY: 'On duty',
  RESTING: 'Resting',
  REST_COMPLETE: 'Rest complete',
  NO_PRIOR_DUTY: 'No prior duty'
};

const FLAG_COLOR = {
  OVER_LIMIT: 'error',
  APPROACHING: 'warning',
  WEEKLY_REST: 'error',
  CHARTER_MISMATCH: 'secondary',
  FATIGUE: 'warning',
  MIXED_FLEET: 'info',
  PART_III: 'warning',
  ON_DUTY: 'warning',
  IAP_SHORT: 'error'
};

function dgcaComplianceChip(item, { onFixMismatch, crewId } = {}) {
  if (item.dgcaCompliant == null) {
    return (
      <Chip
        size="small"
        label={item.match === 'CHARTER_NO_DUTY' && onFixMismatch ? 'N/A — Fix' : 'N/A'}
        color="default"
        icon={item.match === 'CHARTER_NO_DUTY' && onFixMismatch ? <Edit /> : undefined}
        clickable={item.match === 'CHARTER_NO_DUTY' && !!onFixMismatch}
        onClick={
          item.match === 'CHARTER_NO_DUTY' && onFixMismatch
            ? () => onFixMismatch({ ...item, crewId })
            : undefined
        }
      />
    );
  }
  if (item.dgcaCompliant) {
    return <Chip size="small" label="Compliant" color="success" />;
  }
  if (item.isPartIiiExtension) {
    return (
      <Tooltip title={(item.nonComplianceType || []).join(', ') || 'Part III extension'}>
        <Chip size="small" label="Part III" color="warning" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={(item.nonComplianceType || []).join(', ') || 'DGCA non-compliant'}>
      <Chip size="small" label="Non-Compliant" color="error" />
    </Tooltip>
  );
}

function bandColor(band) {
  if (band === 'critical') return 'error';
  if (band === 'warn') return 'warning';
  return 'success';
}

function MetricCell({ metric, compact }) {
  if (!metric || metric.used == null) {
    return <Typography variant="body2">—</Typography>;
  }
  const pct = metric.pct != null ? Math.min(100, Math.max(0, metric.pct * 100)) : 0;
  const label =
    metric.limit != null
      ? `${Number(metric.used).toFixed(1)} / ${Number(metric.limit).toFixed(1)}h`
      : `${Number(metric.used).toFixed(1)}h`;
  return (
    <Box sx={{ minWidth: compact ? 88 : 110 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: `${bandColor(metric.band)}.main` }}>
        {label}
      </Typography>
      {metric.limit != null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={bandColor(metric.band)}
          sx={{ height: 6, borderRadius: 1, mt: 0.4 }}
        />
      )}
      {!compact && metric.source === 'COMPANY_POLICY' && (
        <Typography variant="caption" color="text.secondary">
          {metric.policyCode || 'policy'}
        </Typography>
      )}
    </Box>
  );
}

/** VIP currency chip: green/red with PIC hours vs required. */
function VipCurrencyCell({ currency }) {
  if (!currency) {
    return <Typography variant="body2">—</Typography>;
  }
  const needPrefix = currency.minExclusive ? '>' : '≥';
  return (
    <Tooltip
      title={`PIC ${Number(currency.picHours || 0).toFixed(1)}h / need ${needPrefix}${currency.requiredHours}h in last ${currency.windowDays}d`}
    >
      <Chip
        size="small"
        color={currency.ok ? 'success' : 'error'}
        label={`${Number(currency.picHours || 0).toFixed(1)}h`}
      />
    </Tooltip>
  );
}

/** IAP currency chip: green/red with approach count vs required (≥2 in 180d). */
function IapCurrencyCell({ currency }) {
  if (!currency) {
    return <Typography variant="body2">—</Typography>;
  }
  return (
    <Tooltip
      title={`IAP ${currency.iapCount || 0} / need ≥${currency.requiredCount} in last ${currency.windowDays}d (PIC techlog)`}
    >
      <Chip
        size="small"
        color={currency.ok ? 'success' : 'error'}
        label={`${currency.iapCount || 0}`}
      />
    </Tooltip>
  );
}

function rowsToCsv(payload) {
  const headers = [
    'Crew',
    'Status',
    'Type',
    'FT today used',
    'FT today limit',
    'FT 7d used',
    'FT 7d limit',
    'FT 14d used',
    'FT 14d limit',
    'FT 28d used',
    'FT 28d limit',
    'FT 90d used',
    'FT 90d limit',
    'FT 365d used',
    'FT 365d limit',
    'Duty 28d used',
    'Duty 28d limit',
    'VIP PIC 30d',
    'VIP 30d status',
    'VIP PIC 180d',
    'VIP 180d status',
    'IAP count 180d',
    'IAP 180d status',
    'Weekly rest',
    'Linked',
    'Charter no duty',
    'Duty no charter',
    'Flags'
  ];
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(',')];
  for (const r of payload.rows || []) {
    const wr = r.weeklyRest || {};
    lines.push(
      [
        r.crewName,
        r.status,
        r.headlineAircraftType,
        r.flightTime?.today?.used,
        r.flightTime?.today?.limit,
        r.flightTime?.d7?.used,
        r.flightTime?.d7?.limit,
        r.flightTime?.d14?.used,
        r.flightTime?.d14?.limit,
        r.flightTime?.d28?.used,
        r.flightTime?.d28?.limit,
        r.flightTime?.d90?.used,
        r.flightTime?.d90?.limit,
        r.flightTime?.d365?.used,
        r.flightTime?.d365?.limit,
        r.dutyTime?.d28?.used,
        r.dutyTime?.d28?.limit,
        r.vipCurrency?.picHours,
        r.vipCurrency ? (r.vipCurrency.ok ? 'CURRENT' : 'NOT_CURRENT') : '',
        r.vipCurrency180?.picHours,
        r.vipCurrency180 ? (r.vipCurrency180.ok ? 'CURRENT' : 'NOT_CURRENT') : '',
        r.iapCurrency?.iapCount,
        r.iapCurrency ? (r.iapCurrency.ok ? 'CURRENT' : 'NOT_CURRENT') : '',
        wr.applies ? (wr.ok ? 'OK' : 'SHORT') : 'n/a',
        r.trips?.linked,
        r.trips?.charterNoDuty,
        r.trips?.dutyNoCharter,
        (r.flags || []).join('|')
      ]
        .map(esc)
        .join(',')
    );
  }
  return lines.join('\n');
}

const CrewHoursBoard = ({
  crewMembers = [],
  displayTimezone,
  onFixMismatch,
  /** When set, show only this crew's FDTL detail (no all-crew board). Used for CREW self-view. */
  selfCrewId = null
}) => {
  const { showToast } = useToast();
  const [windowDays, setWindowDays] = useState(28);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [aircraftType, setAircraftType] = useState('');
  const [crewId, setCrewId] = useState('');
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPdfLoading, setDetailPdfLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isSelfView = Boolean(selfCrewId);
  const hasCustomRange = Boolean(startDate && endDate);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (hasCustomRange) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else {
      params.set('windowDays', String(windowDays));
    }
    if (aircraftType) params.set('aircraftType', aircraftType);
    if (crewId) params.set('crewId', crewId);
    if (atRiskOnly) params.set('atRiskOnly', 'true');
    return params;
  }, [windowDays, startDate, endDate, hasCustomRange, aircraftType, crewId, atRiskOnly]);

  const periodQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (hasCustomRange) {
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    } else {
      params.set('windowDays', String(windowDays));
    }
    return params;
  }, [windowDays, startDate, endDate, hasCustomRange]);

  const fetchBoard = useCallback(
    async (signal) => {
      if (isSelfView) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/ops/crew-duty-roster/crew-hours?${queryParams}`, {
          headers: authHeaders(),
          signal
        });
        if (signal?.aborted) return;
        setPayload(res.data);
      } catch (error) {
        if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
        console.error('Error fetching crew hours board:', error);
        showToast(error.response?.data?.message || 'Failed to load crew hours', 'error');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [queryParams, showToast, isSelfView]
  );

  const fetchSelfDetail = useCallback(
    async (signal) => {
      if (!selfCrewId) return;
      setDetailLoading(true);
      try {
        const res = await axios.get(
          `${API_URL}/ops/crew-duty-roster/crew-hours/${selfCrewId}?${periodQueryParams}`,
          { headers: authHeaders(), signal }
        );
        if (signal?.aborted) return;
        setDetail(res.data);
      } catch (error) {
        if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
        console.error('Error fetching own crew hours:', error);
        showToast(error.response?.data?.message || 'Failed to load your crew hours', 'error');
        setDetail(null);
      } finally {
        if (!signal?.aborted) setDetailLoading(false);
      }
    },
    [selfCrewId, periodQueryParams, showToast]
  );

  useEffect(() => {
    const controller = new AbortController();
    if (isSelfView) {
      fetchSelfDetail(controller.signal);
    } else {
      fetchBoard(controller.signal);
    }
    return () => controller.abort();
  }, [fetchBoard, fetchSelfDetail, isSelfView]);

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/crew-hours/${row.crewId}?${periodQueryParams}`,
        { headers: authHeaders() }
      );
      setDetail(res.data);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to load crew hours detail', 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!payload) return;
    const blob = new Blob([rowsToCsv(payload)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `crew-hours-board-${periodStamp(hasCustomRange, startDate, endDate)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    setExporting(true);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/crew-hours/export?${queryParams}&format=pdf&download=1`;
      await downloadAuthenticatedPdf(
        url,
        `crew-hours-board-${periodStamp(hasCustomRange, startDate, endDate)}.pdf`
      );
    } catch (error) {
      showToast(error.message || 'Failed to export PDF', 'error');
    } finally {
      setExporting(false);
    }
  };

  const openBoardPdf = async () => {
    setExporting(true);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/crew-hours/export?${queryParams}&format=pdf&download=0`;
      try {
        await viewAuthenticatedPdf(url);
      } catch (err) {
        openAuthenticatedPdfViaToken(
          url.startsWith('/')
            ? url
            : `/api/ops/crew-duty-roster/crew-hours/export?${queryParams}&format=pdf&download=0`
        );
      }
    } catch (error) {
      showToast(error.message || 'Failed to open PDF', 'error');
    } finally {
      setExporting(false);
    }
  };

  const resolvePilotId = () =>
    detail?.row?.crewId || selfCrewId || null;

  const downloadPilotReportPdf = async () => {
    const pilotId = resolvePilotId();
    if (!pilotId) return;
    setDetailPdfLoading(true);
    try {
      const url = `${API_URL}/ops/crew-duty-roster/crew-hours/${pilotId}/report.pdf?${periodQueryParams}&download=1`;
      const safe = String(detail?.row?.crewName || 'pilot').replace(/[^\w.-]+/g, '_').slice(0, 40);
      await downloadAuthenticatedPdf(
        url,
        `pilot-fdtl-report-${safe}-${periodStamp(hasCustomRange, startDate, endDate)}.pdf`
      );
    } catch (error) {
      showToast(error.message || 'Failed to download pilot report PDF', 'error');
    } finally {
      setDetailPdfLoading(false);
    }
  };

  const openPilotReportPdf = async () => {
    const pilotId = resolvePilotId();
    if (!pilotId) return;
    setDetailPdfLoading(true);
    try {
      const path = `/api/ops/crew-duty-roster/crew-hours/${pilotId}/report.pdf?${periodQueryParams}&download=0`;
      const url = `${API_URL}/ops/crew-duty-roster/crew-hours/${pilotId}/report.pdf?${periodQueryParams}&download=0`;
      try {
        await viewAuthenticatedPdf(url);
      } catch (err) {
        openAuthenticatedPdfViaToken(path);
      }
    } catch (error) {
      showToast(error.message || 'Failed to open pilot report PDF', 'error');
    } finally {
      setDetailPdfLoading(false);
    }
  };

  const downloadPilotCsv = async () => {
    const pilotId = resolvePilotId();
    if (!pilotId) return;
    setDetailPdfLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/crew-hours/${pilotId}/export.csv?${periodQueryParams}`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const safe = String(detail?.row?.crewName || 'pilot').replace(/[^\w.-]+/g, '_').slice(0, 40);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute(
        'download',
        `pilot-hours-${safe}-${periodStamp(hasCustomRange, startDate, endDate)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      showToast('Hours CSV downloaded', 'success');
    } catch (error) {
      showToast(error.response?.data?.message || error.message || 'Failed to download CSV', 'error');
    } finally {
      setDetailPdfLoading(false);
    }
  };

  const rows = payload?.rows || [];
  const detailRow = detail?.row;

  const renderDetailBody = (row) => {
    if (!row) return null;
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={REST_LABEL[row.status] || row.status} color={REST_COLOR[row.status]} />
          {(row.flags || []).map((f) => (
            <Chip key={f} size="small" label={f.replace(/_/g, ' ')} color={FLAG_COLOR[f] || 'default'} />
          ))}
        </Stack>
        {row.vipCurrency && (
          <Alert severity={row.vipCurrency.ok ? 'success' : 'error'}>
            VIP Currency (30d): {Number(row.vipCurrency.picHours || 0).toFixed(1)}h as PIC
            {row.vipCurrency.ok
              ? ` (current — more than ${row.vipCurrency.requiredHours ?? 5}h)`
              : ` (not current — need more than ${row.vipCurrency.requiredHours ?? 5}h)`}
          </Alert>
        )}
        {row.vipCurrency180 && (
          <Alert severity={row.vipCurrency180.ok ? 'success' : 'error'}>
            VIP Currency (180d): {Number(row.vipCurrency180.picHours || 0).toFixed(1)}h as PIC
            {row.vipCurrency180.ok
              ? ` (current — at least ${row.vipCurrency180.requiredHours ?? 30}h)`
              : ` (not current — need at least ${row.vipCurrency180.requiredHours ?? 30}h)`}
          </Alert>
        )}
        {row.iapCurrency && (
          <Alert severity={row.iapCurrency.ok ? 'success' : 'error'}>
            IAP Currency (180d): {row.iapCurrency.iapCount || 0} approaches as PIC
            {row.iapCurrency.ok
              ? ` (current — at least ${row.iapCurrency.requiredCount ?? 2})`
              : ` (not current — need at least ${row.iapCurrency.requiredCount ?? 2} in last ${row.iapCurrency.windowDays ?? 180}d)`}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary">
          Rest required {row.restRequiredHours != null ? formatDuration(row.restRequiredHours) : '—'}
          {' · '}accrued {row.restAccruedHours != null ? formatDuration(row.restAccruedHours) : '—'}
          {' · '}remaining {row.restRemainingHours != null ? formatDuration(row.restRemainingHours) : '—'}
          {row.restPeriodEnd ? ` · ends ${formatDateTime(row.restPeriodEnd)}` : ''}
        </Typography>
        {row.weeklyRest?.applies && (
          <Alert severity={row.weeklyRest.ok ? 'success' : 'warning'}>
            Weekly rest: {Number(row.weeklyRest.longestHours || 0).toFixed(1)}h
            {row.weeklyRest.nights != null ? ` (${row.weeklyRest.nights} local nights)` : ''} vs{' '}
            {row.weeklyRest.requiredHours}h required.
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell align="right">Used</TableCell>
              <TableCell align="right">Limit</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ['Flight today', row.flightTime?.today],
              ['Flight 7d', row.flightTime?.d7],
              ['Flight 14d', row.flightTime?.d14],
              ['Flight 28d', row.flightTime?.d28],
              ['Flight 90d', row.flightTime?.d90],
              ['Flight 365d', row.flightTime?.d365],
              ['Duty today', row.dutyTime?.today],
              ['Duty 7d', row.dutyTime?.d7],
              ['Duty 28d', row.dutyTime?.d28]
            ].map(([label, m]) => (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                <TableCell align="right">{m?.used != null ? Number(m.used).toFixed(1) : '—'}</TableCell>
                <TableCell align="right">{m?.limit != null ? Number(m.limit).toFixed(1) : '—'}</TableCell>
                <TableCell align="right">{m?.remaining != null ? Number(m.remaining).toFixed(1) : '—'}</TableCell>
                <TableCell>
                  {m?.source === 'COMPANY_POLICY' ? m.policyCode || 'Company policy' : m?.source || '—'}
                  {m?.dgcaLimit != null && m?.source === 'COMPANY_POLICY' && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      DGCA {Number(m.dgcaLimit).toFixed(1)}h
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {row.mixedFleet && row.byAircraftType && (
          <Alert severity="info">
            Mixed fleet in the 365-day window: {(row.aircraftTypes || []).join(', ')}. Headline uses the
            higher utilisation type ({row.headlineAircraftType}).
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2">Trips in window ({windowDays}d)</Typography>
          <Typography variant="body2" color="text.secondary">
            Flight {row.trips?.totalFlightHours != null ? `${Number(row.trips.totalFlightHours).toFixed(2)}h` : '—'}
            {' · '}
            Duty {row.trips?.totalDutyHours != null ? `${Number(row.trips.totalDutyHours).toFixed(2)}h` : '—'}
          </Typography>
        </Stack>

        {(row.trips?.byTrip || []).length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Aircraft</TableCell>
                <TableCell align="right">Flight hours</TableCell>
                <TableCell align="right">Duty hours</TableCell>
                <TableCell>Match</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.trips.byTrip.map((t, idx) => (
                <TableRow key={t.tripId || t.charterNumber || idx}>
                  <TableCell>{t.date ? formatDateTime(t.date) : '—'}</TableCell>
                  <TableCell>{t.aircraft || '—'}</TableCell>
                  <TableCell align="right">
                    {t.flightHours != null ? `${Number(t.flightHours).toFixed(2)}h` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {t.dutyHours != null ? `${Number(t.dutyHours).toFixed(2)}h` : '—'}
                  </TableCell>
                  <TableCell>
                    {(t.matches || []).map((m) => m.replace(/_/g, ' ')).join(', ') || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Typography variant="subtitle2">Sectors / duties</Typography>
        {(row.trips?.items || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No roster-linked or charter-assigned sectors in this window.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>DGCA</TableCell>
                <TableCell>Charter</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>When</TableCell>
                <TableCell align="right">Flight hours</TableCell>
                <TableCell align="right">Duty hours</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {row.trips.items.map((item, idx) => (
                <TableRow key={`${item.match}-${item.sectorId || item.dutyId || idx}`}>
                  <TableCell>
                    {dgcaComplianceChip(item, {
                      onFixMismatch: isSelfView ? undefined : onFixMismatch,
                      crewId: row.crewId
                    })}
                  </TableCell>
                  <TableCell>
                    {item.charterNumber || item.tripTitle || '—'}
                    {item.seqNo != null && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Sector {item.seqNo}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.route || '—'}</TableCell>
                  <TableCell>{item.departure ? formatDateTime(item.departure) : '—'}</TableCell>
                  <TableCell align="right">
                    {item.flightHours != null ? `${Number(item.flightHours).toFixed(2)}h` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {item.dutyHours != null ? `${Number(item.dutyHours).toFixed(2)}h` : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {item.hoursSource || '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    );
  };

  if (isSelfView) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h6">My Hours / FDTL</Typography>
            <Typography variant="body2" color="text.secondary">
              Your remaining flight and duty hours vs DGCA and company policy.
              {displayTimezone ? ` Times use ${displayTimezone}.` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={hasCustomRange}>
              <InputLabel>Trip window</InputLabel>
              <Select value={windowDays} label="Trip window" onChange={(e) => setWindowDays(Number(e.target.value))}>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={28}>28 days</MenuItem>
                <MenuItem value={45}>45 days</MenuItem>
                <MenuItem value={365}>365 days</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Start date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              type="date"
              label="End date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || undefined }}
              sx={{ width: 150 }}
            />
            {(startDate || endDate) && (
              <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); }} sx={{ textTransform: 'none' }}>
                Clear dates
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadPilotCsv}
              disabled={detailLoading || detailPdfLoading || !resolvePilotId() || (Boolean(startDate) !== Boolean(endDate))}
            >
              CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={openPilotReportPdf}
              disabled={detailLoading || detailPdfLoading || !resolvePilotId() || (Boolean(startDate) !== Boolean(endDate))}
            >
              View PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={downloadPilotReportPdf}
              disabled={detailLoading || detailPdfLoading || !resolvePilotId() || (Boolean(startDate) !== Boolean(endDate))}
            >
              {detailPdfLoading ? 'Preparing…' : 'Download PDF'}
            </Button>
          </Stack>
        </Stack>
        {hasCustomRange && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Custom period {startDate} → {endDate} — view or download your hours for this range.
          </Typography>
        )}
        {Boolean(startDate) !== Boolean(endDate) && (
          <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
            Set both start and end dates to apply a custom period.
          </Typography>
        )}
        <Paper sx={{ p: 2 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !detailRow ? (
            <Alert severity="info">No hours data available for your account yet.</Alert>
          ) : (
            renderDetailBody(detailRow)
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h6">Crew Hours / FDTL</Typography>
          <Typography variant="body2" color="text.secondary">
            Live remaining hours vs DGCA and company policy. Flight time is block time (rolling 7 / 28 / 365 days).
            VIP Currency: &gt;5h PIC / 30d and ≥30h PIC / 180d (green/red).
            IAP: ≥2 approaches / 180d from techlog IAP Count (PIC).
            Trip mismatch is informational — roster is not synced from sector assign.
            {displayTimezone ? ` Times use ${displayTimezone}.` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={downloadCsv}
            disabled={!payload || (Boolean(startDate) !== Boolean(endDate))}
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdf />}
            onClick={downloadPdf}
            disabled={exporting || !payload || (Boolean(startDate) !== Boolean(endDate))}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={openBoardPdf}
            disabled={exporting || !payload || (Boolean(startDate) !== Boolean(endDate))}
          >
            Open PDF
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={hasCustomRange}>
            <InputLabel>Trip window</InputLabel>
            <Select value={windowDays} label="Trip window" onChange={(e) => setWindowDays(Number(e.target.value))}>
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={28}>28 days</MenuItem>
              <MenuItem value={45}>45 days</MenuItem>
              <MenuItem value={365}>365 days</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
            sx={{ width: 150 }}
          />
          {(startDate || endDate) && (
            <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); }} sx={{ textTransform: 'none' }}>
              Clear dates
            </Button>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Aircraft type</InputLabel>
            <Select value={aircraftType} label="Aircraft type" onChange={(e) => setAircraftType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Fixed Wing">Fixed Wing</MenuItem>
              <MenuItem value="Helicopter">Helicopter</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Crew</InputLabel>
            <Select value={crewId} label="Crew" onChange={(e) => setCrewId(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {crewMembers.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.firstName} {c.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={atRiskOnly} onChange={(e) => setAtRiskOnly(e.target.checked)} />}
            label="At risk only"
          />
        </Stack>
        {hasCustomRange && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Custom period {startDate} → {endDate}: board and CSV/PDF downloads use this range for trip matching (FDTL rolling limits still as of end date).
          </Typography>
        )}
        {Boolean(startDate) !== Boolean(endDate) && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            Set both start and end dates to apply a custom period.
          </Typography>
        )}
      </Paper>

      {payload && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
          <Chip size="small" label={`Crew: ${payload.totalCrew ?? rows.length}`} />
          <Chip
            size="small"
            variant="outlined"
            label={
              payload.customRange || (payload.startDate && payload.endDate)
                ? `Period: ${payload.startDate} → ${payload.endDate}`
                : `Window: ${payload.windowDays || windowDays}d`
            }
          />
          <Chip size="small" color="warning" label={`At risk: ${payload.atRiskCount ?? 0}`} />
          {payload.vipCurrency && (
            <>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`VIP 30d current: ${payload.vipCurrency.currentCount ?? 0}`}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`VIP 30d not: ${payload.vipCurrency.notCurrentCount ?? 0}`}
              />
            </>
          )}
          {payload.vipCurrency180 && (
            <>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`VIP 180d current: ${payload.vipCurrency180.currentCount ?? 0}`}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`VIP 180d not: ${payload.vipCurrency180.notCurrentCount ?? 0}`}
              />
            </>
          )}
          {payload.iapCurrency && (
            <>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`IAP 180d current: ${payload.iapCurrency.currentCount ?? 0}`}
              />
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`IAP 180d not: ${payload.iapCurrency.notCurrentCount ?? 0}`}
              />
            </>
          )}
          <Chip size="small" variant="outlined" label={`Warn ≥${Math.round((payload.warnPct || 0.8) * 100)}%`} />
          <Chip size="small" variant="outlined" label={`Critical ≥${Math.round((payload.criticalPct || 0.9) * 100)}%`} />
        </Stack>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">No crew hours in this filter. Adjust window or clear at-risk only.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Crew</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>FT Today</TableCell>
                <TableCell>FT 7d</TableCell>
                <TableCell>FT 14d</TableCell>
                <TableCell>FT 28d</TableCell>
                <TableCell>FT 90d</TableCell>
                <TableCell>FT 365d</TableCell>
                <TableCell>Duty 28d</TableCell>
                <TableCell>VIP 30d</TableCell>
                <TableCell>VIP 180d</TableCell>
                <TableCell>IAP 180d</TableCell>
                <TableCell>Weekly rest</TableCell>
                <TableCell>Trips</TableCell>
                <TableCell>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={String(row.crewId)}
                  hover
                  sx={{ cursor: 'pointer', bgcolor: row.atRisk ? 'action.hover' : undefined }}
                  onClick={() => openDetail(row)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.crewName}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <CrewClearanceChips crew={row.clearances} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {row.headlineAircraftType}
                      {row.mixedFleet ? ' · mixed' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={REST_LABEL[row.status] || row.status} color={REST_COLOR[row.status] || 'default'} />
                    {row.hoursOnDuty != null && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {formatDuration(row.hoursOnDuty)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.flightTime?.today} compact />
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.flightTime?.d7} compact />
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.flightTime?.d14} compact />
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.flightTime?.d28} compact />
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.flightTime?.d90} compact />
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.flightTime?.d365} compact />
                  </TableCell>
                  <TableCell>
                    <MetricCell metric={row.dutyTime?.d28} compact />
                  </TableCell>
                  <TableCell>
                    <VipCurrencyCell currency={row.vipCurrency} />
                  </TableCell>
                  <TableCell>
                    <VipCurrencyCell currency={row.vipCurrency180} />
                  </TableCell>
                  <TableCell>
                    <IapCurrencyCell currency={row.iapCurrency} />
                  </TableCell>
                  <TableCell>
                    {row.weeklyRest?.applies ? (
                      <Chip
                        size="small"
                        color={row.weeklyRest.ok ? 'success' : 'error'}
                        label={
                          row.weeklyRest.ok
                            ? `${Number(row.weeklyRest.longestHours || 0).toFixed(0)}h / ${row.weeklyRest.requiredHours}h`
                            : `Short ${Number(row.weeklyRest.longestHours || 0).toFixed(0)}h`
                        }
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        n/a (helo)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Linked / charter without duty / duty without charter">
                      <Typography variant="body2">
                        {row.trips?.linked ?? 0}
                        {(row.trips?.charterNoDuty || row.trips?.dutyNoCharter) ? (
                          <Typography component="span" variant="caption" color="warning.main">
                            {` · ${row.trips.charterNoDuty || 0}/${row.trips.dutyNoCharter || 0}`}
                          </Typography>
                        ) : null}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(row.flags || []).map((f) => (
                        <Chip key={f} size="small" label={f.replace(/_/g, ' ')} color={FLAG_COLOR[f] || 'default'} variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{detailRow?.crewName || 'Crew hours'}</DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detailRow ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderDetailBody(detailRow)
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Download />}
            onClick={downloadPilotCsv}
            disabled={detailLoading || detailPdfLoading || !resolvePilotId()}
          >
            CSV
          </Button>
          <Button
            startIcon={<OpenInNew />}
            onClick={openPilotReportPdf}
            disabled={detailLoading || detailPdfLoading || !resolvePilotId()}
          >
            View PDF
          </Button>
          <Button
            startIcon={<PictureAsPdf />}
            onClick={downloadPilotReportPdf}
            disabled={detailLoading || detailPdfLoading || !resolvePilotId()}
          >
            {detailPdfLoading ? 'Preparing…' : 'Download PDF'}
          </Button>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewHoursBoard;
